import { chatApi } from "~/common/API/chatApi";
import { usePublisherStore } from "~/store/PublisherModule";
import { simpleApi } from "~/common/API/simpleApi";
import type { ISendMessage, ISendSystemMessage, IMessage } from "~/interfaces/chat";
import { getIdFromJwt, getUserId } from "~/common/helpers";
import { toChatSocial } from "~/common/chatChannels";



interface Ticket {
  id: number;
  theme: string;
  status: string;
  userId: number;
  created: string;
  assigned?: number;
}

interface ChatResponse {
  messages: IMessage[];
  id: number;
}

interface TicketResponse {
  data: Ticket[];
}

interface TicketParams {
  'status[]'?: string[];
  userId?: number;
  limit?: number;
  offset?: number;
}

const TICKET_STATUS = {
  CLOSE: 'CLOSE',
  PROGRESS: 'PROGRESS'
} as const;

export default class ChatService {
  /**
   * Обрабатывает ошибки в сервисе
   * @param error - Ошибка для обработки
   * @param context - Контекст, в котором произошла ошибка
   * @throws Error с информацией об ошибке
   */
  static handleError = (error: unknown, context: string) => {
    console.error(`ChatService - ${context}:`, error);
    throw new Error(`Failed to ${context}: ${error instanceof Error ? error.message : String(error)}`);
  };

  /**
   * Канальный чат «канал + паблишер + рекламодатель»: возвращает существующий
   * или создаёт новый (вызов идемпотентный). С publicationId публикация
   * привязывается к чату.
   */
  static getOrCreateChannelChat = async (data: {
    placeId: number;
    social: string;
    publisherId: number;
    advertiserId: number;
    publicationId?: number;
  }) => {
    return await chatApi("/api/v1/chat/channel", {
      method: "POST",
      body: { ...data, social: toChatSocial(data.social) },
    });
  };

  /**
   * Получает список чатов для текущего пользователя
   * @returns Promise со списком чатов
   * @throws Error если получение чатов не удалось
   */
  static getChats = async (id?: number, page: number = 1, limit: number = 20) => {
    const userId = id || getUserId();
    if (!userId) return;
    const params: any = {
      limit,
      page,
      publisherId: +userId,
    };
    try {
      return await chatApi("/api/v1/chat", { params });
    } catch (error) {
      console.log("ChatService - getChats", error);
    }
  };

  /**
   * Получает публикацию по ID
   * @param id - ID публикации
   * @returns Promise с данными публикации
   * @throws Error если получение публикации не удалось
   */
  static getPublicationById = async (id: number) => {
    const endpoint = "/advert/publication";
    try {
      const response: any = await simpleApi(endpoint, { params: { id: id } });
      return response;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  /**
   * Получает сообщения чата по ID
   * @param id - ID чата
   * @returns Promise со списком сообщений
   * @throws Error если получение сообщений не удалось
   */
  static getChatMessagesById = async (id: number): Promise<IMessage[]> => {
    const endpoint = "/api/v1/chat/";
    try {
      const response = await chatApi(`${endpoint}${id}`, {}) as ChatResponse;
      if (!response.messages) return [];

      return response.messages.reverse();
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  /**
   * Получает сообщения чата по ID публикации
   * @param id - ID публикации
   * @returns Promise с объектом, содержащим сообщения и ID чата
   */
  static getChatMessagesByPublicationId = async (id: number): Promise<{ chat: IMessage[], chat_id: number }> => {
    const endpoint = "/api/v1/chat/publication/";
    try {
      const response = await chatApi(`${endpoint}${id}`, {}) as ChatResponse;
      if (!response.messages || response.messages.length < 1)
        return { chat: [], chat_id: response.id };
      return { chat: response.messages.reverse(), chat_id: response.id };
    } catch (err) {
      return { chat: [], chat_id: 0 };
    }
  };

  /**
   * Отправляет сообщение в чат
   * @param data - Данные сообщения
   * @returns Promise с результатом отправки
   * @throws Error если отправка не удалась
   */
  static sendMessage = async (data: ISendMessage) => {
    try {
      const response = await chatApi("/api/v1/chat-message", {
        method: "POST",
        body: data,
      });
      return response;
    } catch (error) {
      console.log("ChatService - sendMessage", error);
    }
  };

  /**
   * Получает тикеты поддержки
   * @param payload - Параметры запроса
   * @returns Promise со списком тикетов
   */
  static getSupportTickets = async (payload?: { userId: number, userType: string }) => {
    const endpoint = "/api/v1/ticket";
    try {
      const response: any = await chatApi(endpoint, { params: payload });
      return response;
    } catch (error) {
      console.log("ChatService - getSupportMessages", error);
      return [];
    }
  };

  /**
   * Создает новый тикет поддержки
   * @param data - Данные для создания тикета
   * @returns Promise с созданным тикетом
   */
  static createSupportTicket = async (data: { userId: number, theme: string }) => {
    const endpoint = "/api/v1/ticket";
    const response = await chatApi(endpoint, {
      method: "POST",
      body: data,
    });
    return response;
  };

  /**
   * Получает историю сообщений пользователя
   * @returns Promise со списком сообщений
   */
  static getUserMessages = async () => {
    const endpoint = "/api/v1/ticket/conversation/history";
    const response: any = await chatApi(endpoint, { params: { limit: 200 } });
    return response.data.reverse();
  };

  /**
   * Отправляет системное сообщение
   * @param data - Данные системного сообщения
   * @returns Promise с результатом отправки
   */
  static sendSystemMessage = async (data: ISendSystemMessage) => {
    const endpoint = "/api/v1/ticket-message";
    const response: any = await chatApi(endpoint, {
      method: "POST",
      body: data,
    });
    return response;
  };

  /**
   * Получает тикеты для администратора
   * @param params - Параметры фильтрации
   * @returns Promise со списком тикетов
   */
  static getTicketsAdmin = async (params: TicketParams): Promise<TicketResponse> => {
    const endpoint = "/api/v1/ticket/";
    const payload = {
      params: {
        ...params,
      },
    };
    const response = await chatApi(endpoint, payload) as TicketResponse;
    return response;
  };

  /**
   * Получает сообщения тикета
   * @param id - ID тикета
   * @returns Promise с данными тикета
   */
  static getTicketMessages = async (id: number): Promise<Ticket> => {
    const endpoint = `/api/v1/ticket/${id}`;
    const response = await chatApi(endpoint, { params: { limit: 20 } }) as Ticket;
    return response;
  };

  /**
   * Закрывает тикет
   * @param ticketId - ID тикета
   * @returns Promise с результатом операции
   */
  static closeTicket = async (ticketId: number) => {
    const endpoint = `/api/v1/ticket/${ticketId}`;
    const response: any = await chatApi(endpoint, {
      method: "PUT",
      body: {
        status: "CLOSE",
      },
    });
    return response;
  };

  /**
   * Открывает тикет
   * @param ticketId - ID тикета
   * @returns Promise с результатом операции
   */
  static openTicket = async (ticketId: number) => {
    const endpoint = `/api/v1/ticket/${ticketId}`;
    const response: any = await chatApi(endpoint, {
      method: "PUT",
      body: {
        status: "PROGRESS",
      },
    });
    return response;
  };

  static rateTicket = async (ticketId: number, rating: number) => {
    const endpoint = `/api/v1/ticket/${ticketId}`;
    const response: any = await chatApi(endpoint, {
      method: "PUT",
      body: { rating },
    });
    return response;
  };

  static readChatUnreadMessages = async (chatId: number) => {
    const endpoint = `/api/v1/chat/read/${chatId}`;
    const response: any = await chatApi(endpoint, {
      method: "PUT",
    });
    return response;
  }

  static readTicketUnreadMessages = async (ticketId: number) => {
    const endpoint = `/api/v1/ticket/read/${ticketId}`;
    const response: any = await chatApi(endpoint, {
      method: "PUT",
    });
    return response;
  }

  static getDialogTicketCount = async () => {
    const endpoint = "/api/v1/ticket/unread/count";
    const response: any = await chatApi(endpoint);
    return response.notRead || 0;
  }
}
